import { useState } from "react";

import { useForm, UseFormReturn } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { FormValues, getValidationSchema } from "./validation";

import { AmountTypeEnum, IbanInvoiceResponseDto } from "api";
import { Bank } from "shared/constants/banks";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  setIsPaymentUrlModalOpen,
  setPaymentUrl,
} from "store/features/ibanInvoicePaymentSlice";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

interface UseIbanInvoiceFormProps {
  iban: IbanInvoiceResponseDto | null;
  handlePayment?: (values?: FormValues, bank?: Bank) => Promise<void>;
}

interface UseIbanInvoiceForm {
  methods: UseFormReturn<FormValues>;
  showAmountInput: boolean;
  showPayerFullName: boolean;
  showOrderNumber: boolean;
  showForm: boolean;
  isLoading: boolean;

  onSubmit?: () => void;

  isPaymentUrlModalOpen: boolean;
  onClosePaymentUrlModal: () => void;
}

export const useIbanInvoiceForm = ({
  iban,
  handlePayment,
}: UseIbanInvoiceFormProps): UseIbanInvoiceForm => {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();
  const isPaymentUrlModalOpen = useAppSelector(
    (state) => state.ibanInvoicePayment.isPaymentUrlModalOpen,
  );

  const { amountType, paymentPurposeOptions } = iban || {};
  const { requestPayerFullName = false, requestOrderNumber = false } =
    paymentPurposeOptions || {};

  const showAmountInput = amountType === AmountTypeEnum.PayerDefined;
  const showPayerFullName = !!requestPayerFullName;
  const showOrderNumber = !!requestOrderNumber;

  const showForm = showAmountInput || showPayerFullName || showOrderNumber;

  const validationSchema = getValidationSchema(
    showAmountInput,
    showPayerFullName,
    showOrderNumber,
  );

  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    values: {
      amount: iban?.amount || 0,
    },
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const onSubmit = methods.handleSubmit((values) => {
    if (handlePayment) {
      setIsLoading(true);
      handlePayment({
        ...values,
        payerFullName: values.payerFullName?.trim(),
        orderNumber: values.orderNumber?.trim(),
      }).finally(() => {
        setIsLoading(false);
      });
    }
  });

  const onClosePaymentUrlModal = () => {
    analytics().sendConversionEvent(
      AnalyticsEvents.iban_qr_close_qr_modal,
      null,
      true,
    );
    dispatch(setIsPaymentUrlModalOpen(false));
    setTimeout(() => {
      dispatch(setPaymentUrl(""));
    }, 500);
  };

  return {
    isLoading,
    methods,
    showForm,
    showAmountInput,
    showPayerFullName,
    showOrderNumber,
    onSubmit,
    isPaymentUrlModalOpen,
    onClosePaymentUrlModal,
  };
};
